"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAppStore } from "@/lib/store";
import { apiGet, apiPost, apiPut } from "@/lib/api-client";
import type { Product } from "@/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { PROPERTY_TYPES, propertyTypeLabel } from "@/components/booking/format";
import { useI18n } from "@/lib/i18n/provider";

export function ProductEditorModal() {
  const {
    productEditorOpen,
    editingProductId,
    closeProductEditor,
    openEditProduct,
  } = useAppStore();
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    location: "",
    propertyType: "HOUSE",
    bedrooms: "1",
    bathrooms: "1",
    maxGuests: "2",
    pricePerNight: "100",
    highSeasonRate: "0",
    familyDiscountPercent: "0",
    amenities: "",
  });

  useEffect(() => {
    if (!productEditorOpen) return;
    if (editingProductId) {
      (async () => {
        try {
          const res = await apiGet<{ product: Product }>(`/api/products/${editingProductId}`);
          setForm({
            title: res.product.title,
            description: res.product.description,
            location: res.product.location,
            propertyType: res.product.propertyType,
            bedrooms: String(res.product.bedrooms),
            bathrooms: String(res.product.bathrooms),
            maxGuests: String(res.product.maxGuests),
            pricePerNight: String(res.product.pricePerNight),
            highSeasonRate: String(res.product.highSeasonRate ?? 0),
            familyDiscountPercent: String(res.product.familyDiscountPercent ?? 0),
            amenities: res.product.amenities,
          });
        } catch (e: any) {
          toast.error(e.message || t("toast.couldNotLoad"));
          closeProductEditor();
        }
      })();
    } else {
      setForm({
        title: "",
        description: "",
        location: "",
        propertyType: "HOUSE",
        bedrooms: "1",
        bathrooms: "1",
        maxGuests: "2",
        pricePerNight: "100",
        highSeasonRate: "0",
        familyDiscountPercent: "0",
        amenities: "",
      });
    }
  }, [productEditorOpen, editingProductId, closeProductEditor, t]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      title: form.title,
      description: form.description,
      location: form.location,
      propertyType: form.propertyType,
      bedrooms: Number(form.bedrooms),
      bathrooms: Number(form.bathrooms),
      maxGuests: Number(form.maxGuests),
      pricePerNight: Number(form.pricePerNight),
      highSeasonRate: Number(form.highSeasonRate),
      familyDiscountPercent: Number(form.familyDiscountPercent),
      amenities: form.amenities,
    };
    try {
      if (editingProductId) {
        await apiPut(`/api/products/${editingProductId}`, payload);
        toast.success(t("toast.propertyUpdated"));
      } else {
        const res = await apiPost<{ product: Product }>("/api/products", payload);
        toast.success(t("toast.propertyCreated"));
        qc.invalidateQueries({ queryKey: ["products"] });
        closeProductEditor();
        openEditProduct(res.product.id);
        return;
      }
      qc.invalidateQueries({ queryKey: ["products"] });
      closeProductEditor();
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotSave"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={productEditorOpen} onOpenChange={(o) => (o ? null : closeProductEditor())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {editingProductId ? t("productEditor.editTitle") : t("productEditor.newTitle")}
          </DialogTitle>
          <DialogDescription>{t("productEditor.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid gap-4">
          <div className="grid gap-1.5">
            <Label>{t("productEditor.title")}</Label>
            <Input
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder={t("productEditor.titlePlaceholder")}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>{t("productEditor.descriptionLabel")}</Label>
            <Textarea
              required
              rows={4}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>{t("productEditor.location")}</Label>
            <Input
              required
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              placeholder={t("productEditor.locationPlaceholder")}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>{t("productEditor.type")}</Label>
              <Select
                value={form.propertyType}
                onValueChange={(v) => setForm((f) => ({ ...f, propertyType: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((ty) => (
                    <SelectItem key={ty} value={ty}>
                      {propertyTypeLabel(ty, locale)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>{t("productEditor.price")}</Label>
              <Input
                type="number"
                min={0}
                required
                value={form.pricePerNight}
                onChange={(e) => setForm((f) => ({ ...f, pricePerNight: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-1.5">
              <Label>{t("productEditor.bedrooms")}</Label>
              <Input
                type="number"
                min={0}
                required
                value={form.bedrooms}
                onChange={(e) => setForm((f) => ({ ...f, bedrooms: e.target.value }))}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("productEditor.bathrooms")}</Label>
              <Input
                type="number"
                min={0}
                required
                value={form.bathrooms}
                onChange={(e) => setForm((f) => ({ ...f, bathrooms: e.target.value }))}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("productEditor.maxGuests")}</Label>
              <Input
                type="number"
                min={1}
                required
                value={form.maxGuests}
                onChange={(e) => setForm((f) => ({ ...f, maxGuests: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>{t("productEditor.amenities")}</Label>
            <Input
              value={form.amenities}
              onChange={(e) => setForm((f) => ({ ...f, amenities: e.target.value }))}
              placeholder={t("productEditor.amenitiesPlaceholder")}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>{t("season.highSeasonRate")}</Label>
              <Input
                type="number"
                min={0}
                value={form.highSeasonRate}
                onChange={(e) => setForm((f) => ({ ...f, highSeasonRate: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">{t("season.highSeasonRateHint")}</p>
            </div>
            <div className="grid gap-1.5">
              <Label>{t("season.familyDiscount")}</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={form.familyDiscountPercent}
                onChange={(e) => setForm((f) => ({ ...f, familyDiscountPercent: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">{t("season.familyDiscountHint")}</p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeProductEditor}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={busy}>
              {busy
                ? t("productEditor.saving")
                : editingProductId
                ? t("productEditor.saveChanges")
                : t("productEditor.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
